import { dashboardTemplates } from '../config/dashboardTemplates';

/** Built-in list without the divider; layout 'portrait' for image templates listed after the divider. */
export function builtInTemplates() {
  const image = dashboardTemplates.imageTemplates;
  const dividerAt = image.findIndex(t => t.isDivider);
  const withLayout = image
    .map((t, i) => ({ ...t, layout: dividerAt >= 0 && i > dividerAt ? 'portrait' : 'landscape' }))
    .filter(t => !t.isDivider);
  return [...withLayout, ...dashboardTemplates.basicTemplates.map(t => ({ ...t, layout: 'landscape' }))];
}

export const TEMPLATE_CATALOG_SCHEMA = 1;
export const BUILT_IN_LANGUAGE = 'en';

const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
// The clock controls this value and it becomes part of a URL (see templatePreviewUrl):
// only accept a plain file-name-shaped string, never a path.
const SAFE_PREVIEW_NAME = /^[A-Za-z0-9._-]+$/;
const stringMap = (raw) => {
  if (!isRecord(raw)) return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) if (typeof v === 'string') out[k] = v;
  return out;
};

/** @returns {{items: Array<{id:string, photos:boolean, layout:'landscape'|'portrait', preview:string, name:Object, description:Object}>}|null} */
export function parseTemplateCatalog(raw) {
  if (!isRecord(raw) || raw.schema !== TEMPLATE_CATALOG_SCHEMA || !Array.isArray(raw.items)) return null;
  const items = [];
  for (const it of raw.items) {
    if (!isRecord(it) || typeof it.id !== 'string') continue;
    items.push({
      id: it.id,
      photos: it.photos === true,
      layout: it.layout === 'portrait' ? 'portrait' : 'landscape',
      preview: typeof it.preview === 'string' && SAFE_PREVIEW_NAME.test(it.preview) ? it.preview : `${it.id}.jpg`,
      name: stringMap(it.name),
      description: stringMap(it.description),
    });
  }
  // An empty result (no items, or every item malformed) is treated as "no catalogue":
  // the picker must never be empty, so the caller falls back to the built-in list.
  if (items.length === 0) return null;
  return { items };
}

const row = (id, item, builtIn, language, available) => ({
  id,
  name: item?.name[language] ?? builtIn?.name ?? item?.name[BUILT_IN_LANGUAGE] ?? id,
  description: item?.description[language] ?? builtIn?.description ?? item?.description[BUILT_IN_LANGUAGE] ?? '',
  photos: item?.photos ?? builtIn?.supportsSlideshow ?? false,
  layout: item?.layout ?? builtIn?.layout ?? 'landscape',
  // The clock didn't report this item, but it's one of ours: Storage holds a preview for
  // every built-in id, so point at it directly (a 404 falls back to the placeholder via onError).
  preview: item?.preview ?? (builtIn ? `${id}.jpg` : null),
  availableOnClock: available,
  builtIn: builtIn ?? null,
});

/**
 * Pure grouping for display: landscape rows first, then portrait rows, with a stable
 * (original) order preserved inside each group. Used so a divider heading can be placed
 * once, before the first portrait row, regardless of the order the clock reported ids in.
 */
export function groupByLayout(rows) {
  const landscape = rows.filter(r => r.layout !== 'portrait');
  const portrait = rows.filter(r => r.layout === 'portrait');
  return [...landscape, ...portrait];
}

/** Spec §3 rules 1–5. `builtInAll` = builtInTemplates() (no divider). */
export function resolveTemplates(builtInAll, catalogues, language, selectedId) {
  const builtIn = builtInAll.filter(t => !t.isDivider);
  const builtInById = new Map(builtIn.map(t => [t.id, t]));
  const reporting = catalogues.filter(c => c !== null && c !== undefined);
  let ids;
  if (reporting.length === 0) {
    ids = builtIn.map(t => t.id);
  } else {
    const first = reporting[0].items.map(i => i.id);
    let allowed = new Set(first);
    for (const c of reporting.slice(1)) {
      const theirs = new Set(c.items.map(i => i.id));
      allowed = new Set([...allowed].filter(id => theirs.has(id)));
    }
    if (catalogues.some(c => c === null || c === undefined)) allowed = new Set([...allowed].filter(id => builtInById.has(id)));
    ids = first.filter(id => allowed.has(id));
  }
  const itemById = new Map();
  for (const c of reporting) for (const i of c.items) if (!itemById.has(i.id)) itemById.set(i.id, i);
  const rows = ids.map(id => row(id, itemById.get(id), builtInById.get(id), language, true));
  if (!ids.includes(selectedId)) {
    // No clock reported a catalogue at all (offline / no link) → we don't know the clock
    // lacks this id, so don't claim it's unavailable. Only a reporting clock that omitted
    // the id makes it genuinely unavailable.
    const availableWhenMissing = reporting.length === 0;
    rows.push(row(selectedId, itemById.get(selectedId), builtInById.get(selectedId), language, availableWhenMissing));
  }
  return rows;
}
